(ns hai.tasks.card
  "Shared task row for the Tasks mini-app cards: the title and the assignee,
   over the deadline and the pill summing up what the task links to
   ([[hai.entity.related]]), on a flat card whose muted edge darkens on hover
   and which, pressed, opens the task over the whole card
   ([[hai.tasks.mini-app/editing-view]]), where its stamp is — for a viewer
   too, who gets it read-only. An owner or admin clicks the assignee to
   reassign the task in place. Required by the desktop and mobile Tasks cards
   and the extension sidebar so the row is one implementation.

   Surface differences are passed in: `:change-fx` rides the reassignment's
   success; `:render-extra-actions`, if supplied, is called
   with the task and rendered left of the related pill; `:link-match` and
   `:on-open-entity` go to the related pill."
  (:require
   [garden.stylesheet]
   [hai.entity.related :as related]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.data :as data]
   [hai.tasks.form :as form]
   [hai.tasks.view :as view]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(def ^:private person-icon (icons/mui-icon :outlined :person))

;; Who the task is assigned to, as a circle of initials: violet for the
;; requester, orange for anyone else, and an empty dashed ring around the person
;; glyph for nobody.
(o/defstyled assignee-avatar :span
  {:display "inline-flex" :align-items "center" :justify-content "center"
   :flex "0 0 auto"
   :box-sizing "border-box"
   :width "26px" :height "26px"
   :border-radius "50%"
   :font-size "11px" :font-weight "700" :letter-spacing "0.02em"
   :user-select "none"}
  [:svg {:width "15px" :height "15px" :fill "currentColor"}]
  [:&.me
   {:background t/--card-violet-soft
    :color t/--card-violet}]
  [:&.other
   {:background t/--card-orange-soft
    :color t/--card-orange
    :border-width "1px" :border-style "solid" :border-color t/--card-orange-45}]
  [:&.nobody
   {:border-width "1px" :border-style "dashed" :border-color t/--note-sage-35
    :color t/--link-muted}]
  ([{:keys [assignee me?]}]
   [:<>
    {:class (cond
              (nil? assignee) "nobody"
              me? "me"
              :else "other")
     :title (or (data/assignee-name assignee) "Unassigned")}
    (or (data/assignee-initials assignee) [person-icon])]))

(o/defstyled task-row :div
  {:padding "14px 18px"
   :display "flex" :flex-direction "column" :gap "10px"
   :position "relative"
   ;; The focus ring follows the card's corners.
   :border-radius "inherit"
   :cursor "pointer"}
  [:.task-row-main
   {:display "flex" :flex-direction "row"
    :gap "14px" :align-items "center"}]
  [:.task-title-block
   {:flex "1" :min-width "0"
    :display "flex" :flex-direction "column" :gap "2px"}]
  [:.task-title
   {:font-size "14px" :font-weight "500" :line-height "1.35"
    :color t/--card-forest
    :word-break "break-word"
    :overflow-wrap "anywhere"}]
  [:.task-title.done {:opacity "0.5" :text-decoration "line-through"}]
  [:.actions
   {:display "flex" :align-items "center" :gap "2px"}]
  ;; Under the title: the deadline at the left, the related pill in the corner,
  ;; and a host's extra actions just before it.
  [:.task-row-foot
   {:display "flex" :align-items "center" :justify-content "flex-end"
    :gap "10px"}
   [view/task-deadline {:margin-right "auto"}]])

;; A task's description, flush beneath the row inside the pressed card, rising in
;; as one continuous sheet. Its left inset lines the text up under the title.
(o/defstyled task-detail :div
  {:padding "8px 18px 16px 48px"
   :border-top "1px solid" :border-top-color t/--card-forest-10
   :animation "task-detail-in 0.22s ease"}
  [:.task-desc
   {:font-size "13px" :line-height "1.55"
    :color t/--card-forest
    :white-space "pre-wrap"
    :opacity "0.8"}]
  [:&.done [:.task-desc {:opacity "0.55"}]])

;; The surface a task sits on. A task holding something back rests as a raised,
;; pressable card and presses down when opened; one with nothing to reveal stays
;; flat. Elevation — not a badge or chevron — is the signal that a task expands.
(o/defstyled task-shell :div
  {:border-radius "12px"
   :transition "transform 0.2s ease, box-shadow 0.2s ease, background 0.16s ease"}
  [:&.flat
   {:background t/--task-card-surface-flat
    :border-radius "13px"
    :transition "box-shadow 160ms ease"}]
  ;; The mini-app rows: the flat surface, always edged — muted at rest — over a
  ;; soft shadow.
  [:&.edged
   {:background t/--task-card-surface-flat
    :border-radius "13px"
    :border-width "1px" :border-style "solid" :border-color t/--card-forest-10
    :box-shadow t/--shadow-2
    :transition "border-color 160ms ease"}]
  [:&.raised
   {:background t/--task-card-surface
    :border-radius "16px"
    :box-shadow t/--task-card-shadow
    :cursor "pointer"}]
  ;; Pressed down: shallow shadow, and the detail extrudes inside the rounded box.
  [:&.raised.open
   {:box-shadow t/--task-card-shadow-pressed
    :overflow "hidden"}]
  ;; Hover cues only where hover exists — a tap's sticky :hover would leave the
  ;; card floating until the next tap elsewhere.
  [:at-media {:hover "hover"}
   ;; Only a collapsed card lifts; the lift is the "press me" cue.
   ["&.raised:not(.open):hover"
    {:transform "translateY(-7px)"
     :box-shadow t/--task-card-shadow-hover}]
   ;; A flat card only draws its edge.
   [:&.flat:hover
    {:box-shadow t/--task-card-ring-flat-hover}]
   ;; An edged card darkens its edge.
   [:&.edged:hover
    {:border-color t/--note-sage-35}]]
  ;; A just-added task holds a sage wash then fades — long enough to catch the
  ;; eye. A relative-positioned overlay keeps it off the card's own background
  ;; and drop shadow, so it works the same on flat rows and raised cards.
  [:&.just-added
   {:position "relative"}
   [:&::after
    {:content "''"
     :position "absolute" :inset "0"
     :border-radius "inherit"
     :pointer-events "none"
     :animation "task-just-added 2s ease-out forwards"}]])

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules just-added-keyframes
  (garden.stylesheet/at-keyframes
   :task-just-added
   ["0%"   {:background "rgba(45,62,42,0.16)"}]
   ["55%"  {:background "rgba(45,62,42,0.16)"}]
   ["100%" {:background "rgba(45,62,42,0)"}]))

(o/defstyled task-group :div
  {:transition "background 0.15s ease"}
  [:&.confirming
   {:background "rgba(177,58,48,0.05)"}])

(o/defstyled delete-confirm :div
  {:display "flex" :align-items "center" :justify-content "flex-end"
   :gap "8px"
   :padding "10px 22px 12px"
   :font-size "13px" :color t/--card-forest
   :border-bottom-width "1px" :border-bottom-style "solid" :border-bottom-color t/--property-3}
  [:.prompt {:margin-right "auto" :color t/--link-muted}]
  [:.delete-yes
   {:padding "6px 14px"
    :border "none" :border-radius "8px"
    :background t/--property-13 :color "white"
    :font-family "inherit"
    :font-size "13px" :font-weight "600"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--property-14}]]
  [:.delete-no
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :font-family "inherit"
    :font-size "13px" :color t/--link-muted
    :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]])

(defn task-row-component
  "One task row for the mini-app cards; the surface-difference opts are
   described in the namespace docstring."
  [task & [{:keys [change-fx render-extra-actions link-match on-open-entity]}]]
  (let [task-uuid (:task/uuid task)
        title (:task/title task)
        done? (some? (:task/completed-at task))
        assignee (:task/assigned-to task)
        deadline? (and (not done?) (some? (:task/deadline-at task)))
        related {:link-urls (vec (keep :activity/url (:task/activities task)))
                 :tasks (:task/tasks task)
                 :notes (:task/notes task)
                 :contacts (:task/contacts task)
                 :tags (:task/tags task)
                 :spaces (:entity/spaces task)}
        related? (boolean (some seq (vals related)))
        extra-actions (when render-extra-actions
                        (render-extra-actions task))
        highlighted? @(<s [::data/highlighted? task-uuid])
        current-user @(<s [:auth/identity])
        open-editor (fn [] (>e [::data/open-editor task-uuid (form/from-task task)]))]
    [task-shell
     {:class (cond-> ["edged"]
               highlighted? (conj "just-added"))}
     [task-row
      {:class (when done? "done")
       :role "button"
       :tab-index 0
       :on-click open-editor
       :on-key-down (fn [e]
                      ;; Only the card's own keys: Enter on the assignee or an
                      ;; action is theirs.
                      (when (and (= (.-target e) (.-currentTarget e))
                                 (contains? #{"Enter" " "} (.-key e)))
                        (.preventDefault e)
                        (open-editor)))}
      [:div.task-row-main
       [:div.task-title-block
        [:div.task-title
         {:class (when done? "done")}
         title]]
       (let [avatar [assignee-avatar {:assignee assignee
                                      :me? (and assignee (data/assignee-me? current-user assignee))}]]
         (if (data/can-assign? task)
           ;; The picker's popover is portalled, but its clicks still bubble
           ;; through the React tree to the card.
           [:div {:on-click #(.stopPropagation %)}
            [assignee-picker/component
             {:editable? true
              :on-pick (fn [picked]
                         (>e [::data/assign task-uuid picked change-fx]))
              :trigger avatar}]]
           avatar))]
      (when (or deadline? related? extra-actions)
        [:div.task-row-foot
         (when deadline?
           [view/task-deadline {:task task}])
         (when extra-actions
           [:div.actions {:on-click #(.stopPropagation %)}
            extra-actions])
         [related/component {:related related
                             :link-match link-match
                             :on-open-entity on-open-entity}]])]]))
